import {isString, isUndefined} from 'lodash';
import {ellipsis} from 'polished';
import React, {FC, useMemo, useState} from 'react';
import styled, {css} from 'styled-components';

import {setTimeout} from '../../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {useConstantAssertion} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {textSelection, visuallyHidden} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {
  makeSearchIdAttribute,
  searchableAttrsSubject,
} from '../../../../../../srcCommon/inboxes/search/searchAttributesHelpers';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {markFullStorySensitive} from '../../../../../helpers/fullstoryHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {makeGetConversationSubject} from '../../../../../store/data/conversations/conversationSubjectSelectors';
import {ConversationHeaderSubjectEditor} from './conversationHeaderSubjectEditor';
import {ConversationSubject} from './conversationSubject';

/*
 * Props.
 */

interface ConversationHeaderSubjectProps {
  cellsContext: CellsContext;
  conversation: ConversationModel;
  onSubjectChange?: (arg: string) => void;
}

/*
 * Style.
 */

interface StyleProps {}

const commonSubjectStyles = css<StyleProps>`
  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.bold};
  ${textSelection()};
`;

const subjectPaddingRight = 16;

interface SubjectStyleProps {
  $canEdit: boolean;
  $isEditing: boolean;
}

const StyledSubjectDiv = styled.div<SubjectStyleProps & StyleProps>`
  ${commonSubjectStyles};
  /* Make text selection easier. */
  line-height: 34px;
  padding: 10px ${subjectPaddingRight}px 10px 16px;
  margin: -10px 0 -10px -16px;
  margin-left: 0;

  ${ellipsis()};
  z-index: 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    background: ${(p) => p.theme.alphas.gray10};
    opacity: 0;
    top: 13px;
    left: 9px;
    z-index: -1;
    width: calc(100% - ${subjectPaddingRight + 1}px);
    height: calc(100% - 26px);
    border-radius: 8px;
  }

  ${(p) => addHoverStyles(p.$canEdit)}
`;

function addHoverStyles(canBeEdited?: boolean) {
  if (!canBeEdited) {
    return '';
  }

  return css`
    &:hover::before {
      opacity: 1;
    }
  `;
}

const StyledConversationHeaderSubjectEditor = styled(ConversationHeaderSubjectEditor)`
  ${commonSubjectStyles};
`;

// Browsers seem to stop at the first element with `user-select: text` next to the subject when triple-clicking.
// So we add our own "empty" element with `user-select: text` so the browser won't try to go select more than the subject.
const StyledSelectionStopGapDiv = styled.div`
  ${visuallyHidden()};
  user-select: text;
`;

/*
 * Component.
 */

export const ConversationHeaderSubject: FC<ConversationHeaderSubjectProps> = (props) => {
  const {
    cellsContext: {teammateId},
    conversation,
    onSubjectChange,
  } = props;

  useConstantAssertion(conversation.id, () => {
    throw new Error('ConversationHeaderSubject is uncontrolled and should be keyed by conversation ID');
  });

  const [isEditing, setIsEditing] = useState(false);
  const subject = useConversationSubject(conversation, teammateId);

  const hasEmptySubject = conversation.subject.trim() === '';
  const canEditSubject = !isUndefined(onSubjectChange);

  const onMouseDown = () => {
    if (isEditing || !canEditSubject) {
      return;
    }

    // Render the editor in response to mouse down to allow drag selection, but
    // defer it to the next tick to avoid the mouse down from blurring the editor.
    setTimeout(() => {
      setIsEditing(true);
    });
  };

  const onDoneEditing = (newSubject: string | null) => {
    if (isString(newSubject) && !isUndefined(onSubjectChange)) {
      onSubjectChange(newSubject);
    }

    setIsEditing(false);
  };

  const subjectRender = <ConversationSubject subject={subject} />;

  return (
    <StyledSubjectDiv
      $isEditing={isEditing}
      $canEdit={canEditSubject}
      className={markFullStorySensitive()}
      onMouseDown={onMouseDown}
      {...searchableAttrsSubject}
      {...makeSearchIdAttribute('subject')}
    >
      {isEditing ? (
        <StyledConversationHeaderSubjectEditor
          initialSubject={subject.text}
          shouldSelectAll={hasEmptySubject}
          onDoneEditing={onDoneEditing}
        />
      ) : (
        <TooltipCoordinator render={() => subjectRender}>
          <TooltipOverflowCondition>
            {subjectRender}
            <StyledSelectionStopGapDiv>&nbsp;</StyledSelectionStopGapDiv>
          </TooltipOverflowCondition>
        </TooltipCoordinator>
      )}
    </StyledSubjectDiv>
  );
};

/*
 * Hooks.
 */

function useConversationSubject(conversation: ConversationModel, teammateId: number) {
  const getConversationSubject = useMemo(
    () =>
      makeGetConversationSubject<void>(
        () => conversation,
        () => teammateId,
      ),
    [conversation, teammateId],
  );
  return useAppSelector(getConversationSubject);
}
